class BrandLogoGridReveal extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFallback();
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — reveal after 5s only if GSAP never initialises */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      /* Poll for GSAP availability — CDN defer scripts may not have executed yet */
      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  async init() {
    await this.waitForGSAP();
    const gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* GSAP loaded — cancel the safety fallback so it doesn't interfere */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('BrandLogoGrid-' + sectionId);
    if (!section) return;

    var logos = section.querySelectorAll('.brand-logo-grid__logo-item');
    if (!logos.length) return;

    var hasScrollTrigger = typeof ScrollTrigger !== 'undefined' ||
                          (gsapLib.plugins && gsapLib.plugins.scrollTrigger);

    if (hasScrollTrigger && gsapLib.registerPlugin) {
      try {
        gsapLib.registerPlugin(ScrollTrigger || gsapLib.plugins.scrollTrigger);
      } catch (e) {
        // ScrollTrigger already registered
      }
    }

    /* Use the logo grid container as ScrollTrigger target so the
       animation fires when the logos (not just the heading) enter view */
    var logoGrid = section.querySelector('.brand-logo-grid__logo-grid');
    var triggerEl = logoGrid || section;

    if (hasScrollTrigger) {
      this.animation = gsapLib.fromTo(logos,
        { opacity: 0, y: 60, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: triggerEl,
            start: 'top 80%',
            once: true
          }
        }
      );
    } else {
      /* Fallback: use IntersectionObserver if ScrollTrigger is not available */
      var self = this;
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            self.animation = gsapLib.fromTo(logos,
              { opacity: 0, y: 60, scale: 0.9 },
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                stagger: 0.12,
                ease: 'power3.out'
              }
            );
            observer.disconnect();
          }
        });
      }, { threshold: 0.2 });
      observer.observe(triggerEl);
    }
  }

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('BrandLogoGrid-' + sectionId);
    if (!section) return;
    var logos = section.querySelectorAll('.brand-logo-grid__logo-item');
    logos.forEach(function(el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  disconnectedCallback() {
    if (this.animation && this.animation.scrollTrigger) {
      this.animation.scrollTrigger.kill();
    }
    if (this.animation && this.animation.kill) {
      this.animation.kill();
    }
  }
}

if (!customElements.get('brand-logo-grid-reveal')) {
  customElements.define('brand-logo-grid-reveal', BrandLogoGridReveal);
}

